import type { ReactElement } from 'react';
import type { MediaBlock } from '@shared/types';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { EditableBullets, EditableText } from '../../components/Editable';
import { DataTablePage } from './DataTablePage';
import { knobClasses } from './knobs';
import { Frame, focalPointStyle, HairlineList } from './primitives';

/**
 * `custom_page` (docs/custom-page.md): one section type, four layouts, all
 * inside the same momentum_v3 `Frame`/footer chrome every other slide uses.
 * `layout === 'table'` (and legacy `data_table`, coerced at load time in
 * src/state/store.ts) delegates whole to `DataTablePage` — that component
 * already reads only `content.table`, never `section.type`, so nothing about
 * it changes here. The other three layouts (`media_full`, `media_split`,
 * `text`) are new in this file.
 *
 * Copy budgets live in `CUSTOM_PAGE_BUDGETS` (src/domain/custom-page.ts) and
 * are enforced server-side (the zod schema rejects an over-budget build/edit
 * before it ever reaches this component) — nothing here re-checks length.
 *
 * Blank slots (docs/custom-page.md "Blank slots"): an editor-only dashed
 * placeholder when a `media_*` layout has no media yet, gated on `!readOnly`
 * exactly like DataTablePage's unconfirmed badge. In `readOnly` (grid
 * thumbnails, print, share) a `media_full`/`media_split` page with no media
 * collapses to the same rendering as `text` — a client must never see an
 * empty frame.
 */

function resolveMediaFit(section: PageProps['section'], media: MediaBlock): 'contain' | 'cover' {
  // "mediaFit knob if set, else from media.kind" (docs/custom-page.md "Media:
  // photo vs. screenshot") — read the RAW style value, not knobValue()'s
  // default-filled result, because the catalog default ('fit') would
  // otherwise mask "the rep never touched this" and always win over kind.
  const raw = section.style?.mediaFit;
  if (raw === 'fit') return 'contain';
  if (raw === 'fill') return 'cover';
  return media.kind === 'photo' ? 'cover' : 'contain';
}

function mediaSide(section: PageProps['section']): 'left' | 'right' {
  return section.style?.mediaSide === 'left' ? 'left' : 'right';
}

/** The dashed editor-only "nothing here yet" frame for an empty media slot. */
function MediaPlaceholder(): ReactElement {
  return (
    <div className="mv3-cp-media-empty">
      <span>Drop an image in the AgentBar</span>
    </div>
  );
}

function MediaFrame({
  media,
  section,
  className,
}: {
  media: MediaBlock;
  section: PageProps['section'];
  className?: string;
}): ReactElement {
  const fit = resolveMediaFit(section, media);
  return (
    <div className={`mv3-cp-media-frame mv3-cp-media-fit-${fit}${className ? ` ${className}` : ''}`}>
      <img
        src={media.url}
        alt={media.alt}
        className="mv3-cp-media-image"
        style={{ objectFit: fit, ...focalPointStyle(section) }}
      />
    </div>
  );
}

/**
 * Shared body for the `text` layout AND the readOnly fallback a `media_*`
 * page with no media collapses to (docs/custom-page.md "Blank slots").
 */
function TextBody({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const headline = section.content.headline;
  const body = section.content.body;
  const bullets = section.content.bullets;

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className={['mv3-cp-root', 'mv3-cp-text-root', ...knobClasses(section)].join(' ')}
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className="mv3-cp-text-body">
          <h1 className="mv3-cp-headline mv3-h1">
            <EditableText
              value={headline}
              field="headline"
              placeholder="Add a headline"
              readOnly={readOnly}
              variant="headline"
              onSave={(next) => actions.saveContent(section, { headline: next })}
            />
          </h1>
          <div className="mv3-cp-text-copy">
            <EditableText
              value={body}
              field="body"
              placeholder="Add body copy"
              readOnly={readOnly}
              onSave={(next) => actions.saveContent(section, { body: next })}
            />
          </div>
          {readOnly ? (
            bullets && bullets.length > 0 ? (
              <HairlineList items={bullets} className="mv3-cp-text-bullets" />
            ) : null
          ) : (
            <EditableBullets
              value={bullets}
              readOnly={readOnly}
              onSave={(next) => actions.saveContent(section, { bullets: next })}
            />
          )}
        </div>
      </Frame>
    </PageShell>
  );
}

function MediaFullLayout(props: PageProps): ReactElement {
  const { proposal, section, actions, readOnly } = props;
  const media = section.content.media ?? null;
  const caption = section.content.caption;

  if (!media && readOnly) return <TextBody {...props} />;

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className={['mv3-cp-root', 'mv3-cp-mediafull-root', ...knobClasses(section)].join(' ')}
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className="mv3-cp-mediafull-body">
          <h1 className="mv3-cp-headline mv3-h1">
            <EditableText
              value={section.content.headline}
              field="headline"
              placeholder="Add a headline"
              readOnly={readOnly}
              variant="headline"
              onSave={(next) => actions.saveContent(section, { headline: next })}
            />
          </h1>
          <div className="mv3-cp-mediafull-frame">
            {media ? <MediaFrame media={media} section={section} /> : <MediaPlaceholder />}
          </div>
          <div className="mv3-cp-caption">
            <EditableText
              value={caption}
              field="body"
              placeholder="Add a caption (optional)"
              readOnly={readOnly}
              onSave={(next) => actions.saveContent(section, { caption: next })}
            />
          </div>
        </div>
      </Frame>
    </PageShell>
  );
}

function MediaSplitLayout(props: PageProps): ReactElement {
  const { proposal, section, actions, readOnly } = props;
  const media = section.content.media ?? null;
  const bullets = section.content.bullets;

  if (!media && readOnly) return <TextBody {...props} />;

  const side = mediaSide(section);

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className={['mv3-cp-root', 'mv3-cp-split-root', ...knobClasses(section)].join(' ')}
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className={`mv3-cp-split-body knob-mediaSide-${side}`}>
          <div className="mv3-cp-split-media">
            {media ? <MediaFrame media={media} section={section} className="mv3-cp-split-media-frame" /> : <MediaPlaceholder />}
          </div>
          <div className="mv3-cp-split-text">
            <h1 className="mv3-cp-headline mv3-h1">
              <EditableText
                value={section.content.headline}
                field="headline"
                placeholder="Add a headline"
                readOnly={readOnly}
                variant="headline"
                onSave={(next) => actions.saveContent(section, { headline: next })}
              />
            </h1>
            <div className="mv3-cp-split-copy">
              <EditableText
                value={section.content.body}
                field="body"
                placeholder="Add body copy"
                readOnly={readOnly}
                onSave={(next) => actions.saveContent(section, { body: next })}
              />
            </div>
            {readOnly ? (
              bullets && bullets.length > 0 ? (
                <HairlineList items={bullets} className="mv3-cp-split-bullets" />
              ) : null
            ) : (
              <EditableBullets
                value={bullets}
                readOnly={readOnly}
                onSave={(next) => actions.saveContent(section, { bullets: next })}
              />
            )}
          </div>
        </div>
      </Frame>
    </PageShell>
  );
}

/** Dispatches on `content.layout` — the entry point registered for `custom_page`. */
export function CustomPage(props: PageProps): ReactElement {
  const layout = props.section.content.layout;
  if (layout === 'media_full') return <MediaFullLayout {...props} />;
  if (layout === 'media_split') return <MediaSplitLayout {...props} />;
  if (layout === 'text') return <TextBody {...props} />;
  // undefined or 'table': a still-coercing data_table section, or an explicit
  // table layout. DataTablePage reads only content.table, so both render
  // identically here (docs/custom-page.md "Folding data_table in").
  return <DataTablePage {...props} />;
}
